import { StatusBadge } from '@evisort/eds/components/StatusBadge/StatusBadge';
import React, { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useStyles } from 'uinix-ui';

import { Box } from '../../components/Box/Box';
import { Link } from '../../components/Link/Link';
import { useHasOverflown } from '../../hooks/useHasOverflown';
import { NavLink } from '../../types/NavLink';
import { Theme } from '../../types/Theme';
import { NavPill } from './NavPill';
import { getBoundedWidthIndex, testIsActiveLink } from './utils';

interface Props {
  activePathname: string;
  links: NavLink[];
  theme: Theme;
}

/**
 * An accessible navigation list of link items (i.e. `nav.ul.li.a`).
 *
 * The `MainNav` autohides overflown nav items in an accessible `ActionsMenu`, implemented by attaching the respective callback to `useHasOverflown` hook (see `testHasOverflown` for relating details).
 *
 * The autohide program is based on `element.offsetWidth`: https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetWidth
 * - Content, padding, border are included.
 * - Margin is not included and the implementation must note to explicitly exclude margins on participating elements in the program.
 * - The DOM does not provide a convenient API that returns the effective width of an element includes the margin.
 */
export const MainNav = ({ activePathname, links, theme = 'dark' }: Props) => {
  const styles = useStyles();
  const intl = useIntl();

  const ref = useRef<HTMLUListElement>(null);

  const isDarkTheme = theme === 'dark';

  const [itemWidths, setItemWidths] = useState<number[]>([]);

  const [visibleItemIndex, setVisibleItemIndex] = useState(links.length);

  // capture item widths
  useEffect(() => {
    const element = ref.current;
    if (element) {
      const itemElements = Array.from(element.children) as HTMLLIElement[];
      setItemWidths(itemElements.map((itemElement) => itemElement.offsetWidth));
    }
  }, [links]);

  const handleAutoHide = () => {
    const element = ref.current;
    if (element) {
      const boundedWidthIndex = getBoundedWidthIndex(
        element.offsetWidth,
        itemWidths,
      );
      setVisibleItemIndex(Math.max(1, boundedWidthIndex)); // at least 1 item must be visible
    }
  };

  useHasOverflown({ callback: handleAutoHide, deps: [itemWidths], ref });

  const visibleLinks = links.slice(0, visibleItemIndex);
  const moreLinks = links.slice(visibleItemIndex);

  const componentStyles = {
    list: {
      alignItems: 'center',
      display: 'flex',
      ...styles.list.unset,
    },
  };

  return (
    <Box
      as="nav"
      aria-label={intl.formatMessage({
        id: 'MainNav.mainNavigationLabel',
        defaultMessage: 'main navigation',
        description: 'Aria label for the main navigation element',
      })}
      flex="auto"
      minW="navbar.min-width"
    >
      <ul ref={ref} style={componentStyles.list}>
        {visibleLinks.map((link) => {
          const { badge, label, pathname, onClick } = link;
          const isActiveLink = testIsActiveLink(activePathname)(link);
          let linkColor:
            | 'text.primary'
            | 'inverse.text.primary'
            | 'text.secondary'
            | 'inverse.text.secondary'
            | undefined;
          if (isActiveLink) {
            linkColor = isDarkTheme ? 'inverse.text.primary' : undefined;
          } else {
            linkColor = isDarkTheme ? 'inverse.text.secondary' : 'text.primary';
          }

          return (
            <li key={pathname}>
              <Link
                isNav
                color={linkColor}
                isActive={isActiveLink}
                pathname={pathname}
                theme={theme}
                variant="navlink"
                onClick={onClick}
              >
                {label}
                {badge && (
                  <Box as="span" pl={2}>
                    <StatusBadge status="informational" text={badge} />
                  </Box>
                )}
              </Link>
            </li>
          );
        })}
        {moreLinks.length > 0 && (
          <NavPill
            icon="more"
            menu={{
              options: moreLinks.map((link) => ({
                ...link,
                value: link.pathname,
              })),
              name: intl.formatMessage({
                id: 'MainNav.moreNavigationMenuName',
                defaultMessage: 'more navigation menu',
                description: 'Name for the overflow navigation menu',
              }),
              offsetY: MENU_OFFSET_Y,
            }}
            text={
              <Box pr={2}>
                {intl.formatMessage({
                  id: 'MainNav.moreButtonText',
                  defaultMessage: 'More',
                  description: 'Text for the more navigation items button',
                })}
              </Box>
            }
            theme={theme}
            tooltip={intl.formatMessage({
              id: 'MainNav.moreItemsTooltip',
              defaultMessage: 'More items',
              description: 'Tooltip text for the more navigation items button',
            })}
          />
        )}
      </ul>
    </Box>
  );
};
/** Offset to Y axis to improve menu visibility in Nav bar */
const MENU_OFFSET_Y = 8;
